import { useState, useEffect } from 'react';

import { faPlug, faCoins, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation, Route, Routes } from 'react-router-dom';

import { Roles } from './Roles';
import { TokenLimits } from './TokenLimits';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import PageWrapper from '@/components/molecules/PageWrappers/PageWrapper';
import { useHasPermission } from '@/hooks/useHasPermission';
import Integrations from '@/screens/Chronos/Configuration/Integrations';

type ConfigurationTab = 'credits' | 'integrations' | 'roles';

const Configuration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useHasPermission();

  const getActiveTab = (): ConfigurationTab => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    return lastSegment === 'integrations' || lastSegment === 'credits' || lastSegment === 'roles'
      ? (lastSegment as ConfigurationTab)
      : 'credits';
  };

  const [activeTab, setActiveTab] = useState<ConfigurationTab>(getActiveTab());

  useEffect(() => {
    const newTab = getActiveTab();
    setActiveTab(newTab);
  }, [location.pathname]);

  const tabs = [
    {
      id: 'credits' as ConfigurationTab,
      permission: Permission.TokenLimitsManage,
      title: 'Credit Limits',
      icon: faCoins,
    },
    {
      id: 'integrations' as ConfigurationTab,
      permission: Permission.IntegrationsManage,
      title: 'Integrations',
      icon: faPlug,
    },
    {
      id: 'roles' as ConfigurationTab,
      permission: Permission.UsersManage,
      title: 'Roles & Permissions',
      icon: faUserShield,
    },
  ];

  // Handle tab navigation with URL updates
  const handleTabChange = (tabId: ConfigurationTab) => {
    setActiveTab(tabId);
    navigate(`/app/chronos/configuration/${tabId}`);
  };

  return (
    <PageWrapper className="bg-gray-50">
      <div className="flex w-full flex-col items-start justify-start px-4 md:px-6 lg:px-8">
        <div className="mb-8 flex w-full flex-col gap-6">
          <div className="w-full rounded-xl bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col">
                <h1 className="text-xl font-semibold text-gray-800">Configuration</h1>
                <p className="text-sm text-gray-600">Manage your organization's settings and integrations.</p>
              </div>

              {/* Tab navigation */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => {
                    if (!hasPermission(tab.permission)) {
                      return null;
                    }

                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={tab.icon}
                          className={`text-sm ${
                            activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                          }`}
                        />
                        {tab.title}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          <div className="w-full">
            <Routes>
              {<Route path="credits" element={<TokenLimits />} />}
              {<Route path="integrations" element={<Integrations />} />}
              {<Route path="roles" element={<Roles />} />}
            </Routes>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Configuration;
